import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, RefreshCw, Loader, TrendingUp,
  UserPlus, X,
  Crown, Shield, Send, Trash2, Clock, AlertCircle, Info,
  SlidersHorizontal, Check, Square, CheckSquare
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useToast } from './Toast';
import { useTheme } from '../context/ThemeContext';
import InviteMemberModal from './InviteMemberModal';
import PartnerPortfolio from './PartnerPortfolio';

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return '?';
  const trimmed = String(nameOrEmail).trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  const local = trimmed.split('@')[0];
  return local.slice(0, 2).toUpperCase();
}

function colorForString(input) {
  const palette = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444'];
  if (!input) return palette[0];
  let hash = 0;
  for (let i = 0; i < input.length; i++) hash = input.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

function formatRelative(timestamp) {
  if (!timestamp) return '';
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = then - Date.now();
  const past = diffMs <= 0;
  const absSec = Math.max(0, Math.floor(Math.abs(diffMs) / 1000));
  if (absSec < 60) return past ? 'just now' : 'in less than a minute';
  const absMin = Math.floor(absSec / 60);
  if (absMin < 60) return past ? `${absMin}m ago` : `in ${absMin}m`;
  const absHr = Math.floor(absMin / 60);
  if (absHr < 24) return past ? `${absHr}h ago` : `in ${absHr}h`;
  const absDay = Math.floor(absHr / 24);
  if (absDay < 7) return past ? `${absDay}d ago` : `in ${absDay}d`;
  return new Date(timestamp).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

function RoleBadge({ role, theme }) {
  const config = {
    owner: { label: 'Owner', color: '#8b5cf6', Icon: Crown },
    member: { label: 'Member', color: '#3b82f6', Icon: Shield },
    viewer: { label: 'Viewer', color: '#6b7280', Icon: Shield }
  }[role] || { label: role, color: '#6b7280', Icon: Shield };

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: `${config.color}1f`,
      color: config.color,
      padding: '3px 8px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'capitalize'
    }}>
      <config.Icon size={11} /> {config.label}
    </span>
  );
}

function Avatar({ label, size = 36, accent }) {
  const initials = getInitials(label);
  const bg = accent || colorForString(label);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `linear-gradient(135deg, ${bg} 0%, ${bg}cc 100%)`,
      color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.36,
      flexShrink: 0
    }}>
      {initials}
    </div>
  );
}

export default function PartnerDashboard() {
  const { showToast } = useToast();
  const { theme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // Bumped after a successful "Sync Ad Spend" so <PartnerPortfolio /> remounts
  // and re-fetches its data. The portfolio component owns its own loading UI.
  const [portfolioRefreshKey, setPortfolioRefreshKey] = useState(0);
  const [organization, setOrganization] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // { userId, email, role }
  const [manageMember, setManageMember] = useState(null); // member whose account access is being edited
  const [confirmRevoke, setConfirmRevoke] = useState(null); // { id, email }
  const [resendingId, setResendingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const cardStyle = {
    background: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 12,
    padding: 20
  };

  const inputStyle = {
    flex: 1,
    background: theme.colors.bg.tertiary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 8,
    padding: '10px 14px',
    color: theme.colors.text.primary,
    fontSize: 13,
    outline: 'none'
  };

  const fetchOrganization = useCallback(async () => {
    try {
      const response = await apiCall('/api/partner/organization');
      const data = await response.json();
      if (data.success) {
        setOrganization(data.organization);
      }
    } catch (err) {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrganization(); }, [fetchOrganization]);

  const handleCreateOrg = async () => {
    if (!orgName.trim()) return;
    setCreatingOrg(true);
    try {
      const response = await apiCall('/api/partner/organization/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: orgName.trim() })
      });
      const data = await response.json();
      if (data.success) {
        showToast('Organization created', 'success');
        setOrgName('');
        await fetchOrganization();
      } else {
        throw new Error(data.error || 'Failed');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!confirmRemove) return;
    const memberId = confirmRemove.userId;
    setRemovingId(memberId);
    try {
      const response = await apiCall('/api/partner/organization/remove-member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId })
      });
      const data = await response.json();
      if (data.success) {
        showToast('Member removed', 'success');
        setConfirmRemove(null);
        await fetchOrganization();
      } else {
        throw new Error(data.error || 'Failed');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRemovingId(null);
    }
  };

  const handleResendInvite = async (inviteId) => {
    setResendingId(inviteId);
    try {
      const response = await apiCall(`/api/partner/organization/invites/${inviteId}/resend`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        showToast(
          data.emailSent ? 'Invitation re-sent' : 'Invitation refreshed (could not deliver email)',
          data.emailSent ? 'success' : 'warning'
        );
        await fetchOrganization();
      } else {
        throw new Error(data.error || 'Failed');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setResendingId(null);
    }
  };

  const handleRevokeInvite = async () => {
    if (!confirmRevoke) return;
    const inviteId = confirmRevoke.id;
    setRevokingId(inviteId);
    try {
      const response = await apiCall(`/api/partner/organization/invites/${inviteId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        showToast('Invitation revoked', 'success');
        setConfirmRevoke(null);
        await fetchOrganization();
      } else {
        throw new Error(data.error || 'Failed');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRevokingId(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await apiCall('/api/partner/sync-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        showToast(`Synced ${data.syncedAccounts} accounts successfully`, 'success');
        // Force PartnerPortfolio to remount + re-fetch so the partner sees
        // any newly-linked clients in their portfolio table.
        setPortfolioRefreshKey(k => k + 1);
      } else {
        throw new Error(data.error || 'Sync failed');
      }
    } catch (err) {
      showToast(`Sync failed: ${err.message}`, 'error');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: theme.colors.text.tertiary }}>
        <Loader size={24} className="animate-spin" style={{ marginRight: 12 }} />
        Loading partner dashboard...
      </div>
    );
  }

  const totalMemberCount = (organization?.members?.length || 0);
  const pendingInvites = organization?.pendingInvites || [];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: theme.colors.text.primary, fontSize: 24, fontWeight: 700, margin: 0 }}>Partner Dashboard</h1>
          <p style={{ color: theme.colors.text.tertiary, fontSize: 14, marginTop: 4 }}>
            Manage your client accounts and billing overview
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600,
            cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? 0.6 : 1
          }}
        >
          {syncing ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {syncing ? 'Syncing...' : 'Sync Ad Spend'}
        </button>
      </div>

      {/* Team / Organization */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'rgba(139, 92, 246, 0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Users size={17} color="#8b5cf6" />
            </div>
            <div>
              <div style={{ color: theme.colors.text.primary, fontSize: 16, fontWeight: 700 }}>Team Members</div>
              <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginTop: 2 }}>
                {organization
                  ? `${totalMemberCount} ${totalMemberCount === 1 ? 'member' : 'members'}${pendingInvites.length > 0 ? ` · ${pendingInvites.length} pending invitation${pendingInvites.length === 1 ? '' : 's'}` : ''}`
                  : 'Create an organization to invite teammates'
                }
              </div>
            </div>
          </div>

          {organization?.isOwner && (
            <button
              onClick={() => setShowInviteModal(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#8b5cf6',
                border: '1px solid #8b5cf6',
                color: '#fff',
                borderRadius: 8,
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(139, 92, 246, 0.25)'
              }}
            >
              <UserPlus size={15} /> Invite team member
            </button>
          )}
        </div>

        {!organization ? (
          <div style={{
            padding: 18,
            background: theme.colors.bg.tertiary,
            border: `1px dashed ${theme.colors.border.primary}`,
            borderRadius: 10
          }}>
            <p style={{ color: theme.colors.text.secondary, fontSize: 13, marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
              Create an organization for your agency. You can then invite teammates who will also
              have access to this Partner Dashboard.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateOrg()}
                placeholder="Your agency name (e.g. Acme Marketing)"
                style={inputStyle}
              />
              <button
                onClick={handleCreateOrg}
                disabled={creatingOrg || !orgName.trim()}
                style={{
                  background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600,
                  cursor: !orgName.trim() ? 'not-allowed' : 'pointer', opacity: !orgName.trim() ? 0.5 : 1
                }}
              >
                {creatingOrg ? 'Creating…' : 'Create organization'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Members list */}
            <div style={{
              border: `1px solid ${theme.colors.border.secondary}`,
              borderRadius: 10,
              overflow: 'hidden',
              marginBottom: pendingInvites.length > 0 ? 16 : 0
            }}>
              {organization.members.map((member, idx) => (
                <div key={member.userId} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px',
                  borderBottom: idx < organization.members.length - 1
                    ? `1px solid ${theme.colors.border.secondary}`
                    : 'none',
                  background: theme.colors.bg.secondary
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <Avatar label={member.fullName || member.email} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 600 }}>
                          {member.fullName || member.email.split('@')[0]}
                        </span>
                        <RoleBadge role={member.role} theme={theme} />
                      </div>
                      <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginTop: 2 }}>
                        {member.email}
                        {member.joinedAt && (
                          <span> · member since {new Date(member.joinedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {organization.isOwner && member.role !== 'owner' && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button
                        onClick={() => setManageMember(member)}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${theme.colors.border.primary}`,
                          borderRadius: 6,
                          color: theme.colors.text.secondary,
                          cursor: 'pointer',
                          padding: '6px 8px',
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontSize: 12, fontWeight: 500
                        }}
                        title="Manage which accounts this member can see"
                      >
                        <SlidersHorizontal size={13} /> Accounts
                      </button>
                      <button
                        onClick={() => setConfirmRemove({ userId: member.userId, email: member.email, role: member.role })}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${theme.colors.border.primary}`,
                          borderRadius: 6,
                          color: theme.colors.text.muted,
                          cursor: 'pointer',
                          padding: '6px 8px',
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontSize: 12, fontWeight: 500
                        }}
                        title="Remove from team"
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pending invites */}
            {pendingInvites.length > 0 && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 8
                }}>
                  <Clock size={14} color={theme.colors.text.tertiary} />
                  <span style={{ color: theme.colors.text.secondary, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Pending invitations ({pendingInvites.length})
                  </span>
                </div>
                <div style={{
                  border: `1px solid ${theme.colors.border.secondary}`,
                  borderRadius: 10,
                  overflow: 'hidden'
                }}>
                  {pendingInvites.map((inv, idx) => {
                    const expired = inv.expiresAt && new Date(inv.expiresAt).getTime() < Date.now();
                    return (
                      <div key={inv.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12,
                        padding: '12px 14px',
                        borderBottom: idx < pendingInvites.length - 1
                          ? `1px solid ${theme.colors.border.secondary}`
                          : 'none',
                        background: theme.mode === 'light'
                          ? 'rgba(245, 158, 11, 0.05)'
                          : 'rgba(245, 158, 11, 0.06)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                          <Avatar label={inv.email} accent="#f59e0b" />
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'
                            }}>
                              <span style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 600 }}>
                                {inv.email}
                              </span>
                              <RoleBadge role={inv.role} theme={theme} />
                              <span style={{
                                background: expired ? 'rgba(239, 68, 68, 0.18)' : 'rgba(245, 158, 11, 0.18)',
                                color: expired ? '#ef4444' : '#f59e0b',
                                fontSize: 10, fontWeight: 600,
                                padding: '2px 8px', borderRadius: 6,
                                textTransform: 'uppercase', letterSpacing: 0.4
                              }}>
                                {expired ? 'Expired' : 'Pending'}
                              </span>
                            </div>
                            <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginTop: 2 }}>
                              Invited {formatRelative(inv.createdAt)}
                              {inv.sendCount > 1 && <span> · sent {inv.sendCount}×</span>}
                              {inv.expiresAt && !expired && (
                                <span> · expires {formatRelative(inv.expiresAt)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={() => handleResendInvite(inv.id)}
                            disabled={resendingId === inv.id}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              borderRadius: 6, padding: '6px 10px',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer'
                            }}
                            title="Resend invitation"
                          >
                            {resendingId === inv.id ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                            Resend
                          </button>
                          <button
                            onClick={() => setConfirmRevoke({ id: inv.id, email: inv.email })}
                            disabled={revokingId === inv.id}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: 'transparent',
                              color: theme.colors.text.muted,
                              border: `1px solid ${theme.colors.border.primary}`,
                              borderRadius: 6, padding: '6px 10px',
                              fontSize: 12, fontWeight: 500, cursor: 'pointer'
                            }}
                            title="Revoke invitation"
                          >
                            <X size={12} /> Revoke
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty-team helper */}
            {organization.isOwner && totalMemberCount === 1 && pendingInvites.length === 0 && (
              <div style={{
                marginTop: 14,
                padding: '12px 14px',
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.25)',
                borderRadius: 10,
                fontSize: 13,
                color: theme.colors.text.secondary,
                display: 'flex', alignItems: 'flex-start', gap: 10
              }}>
                <Info size={15} color="#3b82f6" style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                  You're currently the only team member. Click <strong>"Invite team member"</strong> to
                  add a colleague — they'll receive an email with an invite link.
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Portfolio-based billing — V2: per-client subscription with volume discount.
          We pass a refresh key so a successful "Sync Ad Spend" remounts this
          component and re-fetches the portfolio. */}
      {organization?.isOwner && <PartnerPortfolio key={portfolioRefreshKey} />}

      {/* Client tier pricing reference */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <TrendingUp size={16} color={theme.colors.text.tertiary} />
          <span style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 600 }}>Client Tier Pricing (per managed account)</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[
            { tier: 'Starter', spend: '< €5K', price: '€99/mo' },
            { tier: 'Growth', spend: '€5K - €20K', price: '€249/mo' },
            { tier: 'Scale', spend: '€20K - €100K', price: '€399/mo' },
            { tier: 'Enterprise', spend: '€100K+', price: '€799/mo' },
          ].map(t => (
            <div key={t.tier} style={{ background: theme.colors.bg.tertiary, borderRadius: 8, padding: 12, textAlign: 'center' }}>
              <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600 }}>{t.tier}</div>
              <div style={{ color: theme.colors.text.muted, fontSize: 11, marginTop: 4 }}>{t.spend}</div>
              <div style={{ color: '#10b981', fontSize: 14, fontWeight: 700, marginTop: 6 }}>{t.price}</div>
            </div>
          ))}
        </div>
        <div style={{ color: theme.colors.text.muted, fontSize: 12, marginTop: 12, textAlign: 'center' }}>
          Your partner discount (17,5% – 27,5% based on active accounts) is automatically applied to your client invoices.
        </div>
      </div>

      {/* Invite Modal */}
      <InviteMemberModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        organizationName={organization?.name}
        onInvited={fetchOrganization}
      />

      {/* Confirm Remove Member */}
      {confirmRemove && (
        <ConfirmModal
          theme={theme}
          icon={<Trash2 size={20} color="#ef4444" />}
          accent="#ef4444"
          title={`Remove ${confirmRemove.email}?`}
          description="This person will immediately lose access to the Partner Dashboard. You can invite them again later."
          confirmLabel={removingId ? 'Removing…' : 'Yes, remove'}
          confirmDisabled={!!removingId}
          onConfirm={handleRemoveMember}
          onCancel={() => setConfirmRemove(null)}
        />
      )}

      {/* Manage member account access */}
      {manageMember && (
        <ManageAccountsModal
          theme={theme}
          member={manageMember}
          showToast={showToast}
          onClose={() => setManageMember(null)}
          onSaved={() => { setManageMember(null); }}
        />
      )}

      {/* Confirm Revoke Invite */}
      {confirmRevoke && (
        <ConfirmModal
          theme={theme}
          icon={<AlertCircle size={20} color="#f59e0b" />}
          accent="#f59e0b"
          title={`Revoke invitation for ${confirmRevoke.email}?`}
          description="The link we sent will stop working. You can send a new invitation later."
          confirmLabel={revokingId ? 'Revoking…' : 'Yes, revoke'}
          confirmDisabled={!!revokingId}
          onConfirm={handleRevokeInvite}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}
    </div>
  );
}

function ConfirmModal({ theme, icon, accent, title, description, confirmLabel, confirmDisabled, onConfirm, onCancel }) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 14,
          width: '100%',
          maxWidth: 420,
          padding: 22,
          boxShadow: theme.mode === 'light'
            ? '0 24px 48px rgba(0,0,0,0.18)'
            : '0 24px 48px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 8,
            background: `${accent}20`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0
          }}>
            {icon}
          </div>
          <div>
            <div style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 700 }}>{title}</div>
            <div style={{ color: theme.colors.text.secondary, fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              {description}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: 8, padding: '8px 14px',
              fontSize: 13, fontWeight: 600,
              color: theme.colors.text.secondary, cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            style={{
              background: accent,
              border: `1px solid ${accent}`,
              color: '#fff',
              borderRadius: 8, padding: '8px 16px',
              fontSize: 13, fontWeight: 600,
              cursor: confirmDisabled ? 'not-allowed' : 'pointer',
              opacity: confirmDisabled ? 0.6 : 1
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageAccountsModal({ theme, member, showToast, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState('all');
  const [accounts, setAccounts] = useState([]); // [{ customerId, accountName, allowed }]
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);

  const memberLabel = member.fullName || member.email;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiCall(`/api/partner/organization/members/${member.userId}/accounts`);
        const json = await res.json();
        if (cancelled) return;
        if (!json.success) throw new Error(json.error || 'Could not load access');
        setScope(json.member?.accountScope || 'all');
        setAccounts(json.availableAccounts || []);
        setSelected(new Set((json.availableAccounts || []).filter(a => a.allowed).map(a => a.customerId)));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [member.userId]);

  const toggle = (customerId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiCall(`/api/partner/organization/members/${member.userId}/accounts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope,
          customerIds: scope === 'restricted' ? Array.from(selected) : []
        })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Could not save');
      showToast('Account access updated', 'success');
      onSaved();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const ScopeOption = ({ value, title, desc }) => {
    const active = scope === value;
    return (
      <button
        onClick={() => setScope(value)}
        style={{
          flex: 1, textAlign: 'left', cursor: 'pointer',
          background: active ? 'rgba(139,92,246,0.10)' : theme.colors.bg.tertiary,
          border: `1px solid ${active ? '#8b5cf6' : theme.colors.border.primary}`,
          borderRadius: 10, padding: '12px 14px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
            border: `2px solid ${active ? '#8b5cf6' : theme.colors.text.muted}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#8b5cf6' }} />}
          </div>
          <span style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600 }}>{title}</span>
        </div>
        <div style={{ color: theme.colors.text.muted, fontSize: 12, marginTop: 4, paddingLeft: 24 }}>{desc}</div>
      </button>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 14, width: '100%', maxWidth: 500,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: theme.mode === 'light' ? '0 24px 48px rgba(0,0,0,0.18)' : '0 24px 48px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: 22, paddingBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 8, background: 'rgba(139,92,246,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <SlidersHorizontal size={18} color="#8b5cf6" />
            </div>
            <div>
              <div style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 700 }}>Account access</div>
              <div style={{ color: theme.colors.text.secondary, fontSize: 13, marginTop: 4 }}>
                Choose which accounts <strong>{memberLabel}</strong> can see.
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.text.muted, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '0 22px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
              <Loader size={18} className="spin" /> Loading…
            </div>
          ) : error ? (
            <div style={{
              padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 10, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <ScopeOption value="all" title="All accounts" desc="Can see every account in the organization" />
                <ScopeOption value="restricted" title="Specific accounts" desc="Only the accounts you select below" />
              </div>

              {scope === 'restricted' && (
                <div style={{ marginBottom: 12 }}>
                  {accounts.length === 0 ? (
                    <div style={{
                      padding: 14, background: theme.colors.bg.tertiary, borderRadius: 10,
                      color: theme.colors.text.muted, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8
                    }}>
                      <Info size={15} /> No client accounts in this organization yet. Sync your MCC accounts first.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ color: theme.colors.text.tertiary, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          {selected.size} of {accounts.length} selected
                        </span>
                        <button
                          onClick={() => setSelected(selected.size === accounts.length ? new Set() : new Set(accounts.map(a => a.customerId)))}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#8b5cf6', fontSize: 12, fontWeight: 600, padding: 0 }}
                        >
                          {selected.size === accounts.length ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                      <div style={{ border: `1px solid ${theme.colors.border.secondary}`, borderRadius: 10, overflow: 'hidden' }}>
                        {accounts.map((acc, idx) => {
                          const checked = selected.has(acc.customerId);
                          return (
                            <button
                              key={acc.customerId}
                              onClick={() => toggle(acc.customerId)}
                              style={{
                                width: '100%', textAlign: 'left', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '11px 14px', background: 'transparent', border: 'none',
                                borderBottom: idx < accounts.length - 1 ? `1px solid ${theme.colors.border.secondary}` : 'none'
                              }}
                            >
                              <span style={{ color: checked ? '#8b5cf6' : theme.colors.text.muted, display: 'flex' }}>
                                {checked ? <CheckSquare size={18} /> : <Square size={18} />}
                              </span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: 'block', color: theme.colors.text.primary, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {acc.accountName}
                                </span>
                                <span style={{ display: 'block', color: theme.colors.text.tertiary, fontSize: 11 }}>{acc.customerId}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 22, paddingTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
              color: theme.colors.text.secondary, cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#8b5cf6', border: '1px solid #8b5cf6', color: '#fff',
              borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
              cursor: (saving || loading) ? 'not-allowed' : 'pointer', opacity: (saving || loading) ? 0.6 : 1
            }}
          >
            {saving ? <Loader size={14} className="spin" /> : <Check size={14} />}
            {saving ? 'Saving…' : 'Save access'}
          </button>
        </div>
      </div>
    </div>
  );
}
